import { truncateAddress } from '@kwenta/sdk/utils'
import ConnectedIcon from 'assets/svg/futures/connected-icon.svg'
import DelegationIcon from 'assets/svg/futures/delegation-icon.svg'
import DisconnectedIcon from 'assets/svg/futures/disconnected-icon.svg'
import NoDataIcon from 'assets/svg/futures/no-data.svg'
import SelectedAccountIcon from 'assets/svg/futures/selected-account-icon.svg'
import { Body } from 'components/Text'
import {
	FlexDivCol,
	FlexDivColCentered,
	FlexDivRow,
	FlexDivRowCentered,
} from 'components/layout/flex'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { selectSelectedSubAccountAddress } from 'state/futures/common/selectors'
import { setSelectedSubAccountAddress } from 'state/futures/reducer'
import { selectSubAccountsForAccount } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { resetWalletAddress } from 'state/wallet/actions'
import { selectSignerWallet } from 'state/wallet/selectors'
import styled from 'styled-components'
import media from 'styles/media'
import { getAddress } from 'viem'
import type { Address } from 'viem/accounts'

const SubAccountsManagement = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const subAccountList = useAppSelector(selectSubAccountsForAccount)
	const selectedSubAccountAddress = useAppSelector(selectSelectedSubAccountAddress)
	const signerWallet = useAppSelector(selectSignerWallet)

	const handleSelectedSubAccount = useCallback(
		(address: Address) => {
			dispatch(setSelectedSubAccountAddress(address))
			dispatch(resetWalletAddress({ address, selectedType: 'sub_account' }))
		},
		[dispatch]
	)

	const handleResetSubAccount = useCallback(() => {
		dispatch(resetWalletAddress({ address: signerWallet, selectedType: 'signer' }))
		dispatch(setSelectedSubAccountAddress(''))
	}, [dispatch, signerWallet])

	const handleRowClick = useCallback(
		(address: string) => {
			return selectedSubAccountAddress === address
				? handleResetSubAccount()
				: handleSelectedSubAccount(getAddress(address) as Address)
		},
		[selectedSubAccountAddress, handleResetSubAccount, handleSelectedSubAccount]
	)

	return (
		<>
			<FlexDivCol rowGap="8px">
				<FlexDivRowCentered>
					<Body color="primary" size="large" weight="bold">
						{t('futures.market.trade.margin.modal.delegation.delegated-access')}
					</Body>
				</FlexDivRowCentered>
			</FlexDivCol>
			<FlexDivCol>
				{subAccountList.length === 0 ? (
					<NoDataContainer rowGap="20px">
						<NoDataIcon />
						<Body>{t('futures.market.trade.margin.modal.delegation.no-sub-accounts')}</Body>
					</NoDataContainer>
				) : (
					subAccountList.map(({ address, nickname }) => (
						<SubAccountRowContainer
							key={address}
							justifyContent="space-between"
							onClick={() => handleRowClick(address)}
						>
							<FlexDivRow justifyContent="flexStart" columnGap="12px">
								{address === selectedSubAccountAddress ? (
									<SelectedAccountIcon height={32} width={32} />
								) : (
									<DelegationIcon height={32} width={32} />
								)}
								<FlexDivCol rowGap="5px">
									<Body weight="bold" color="primary">
										{nickname ||
											t('futures.market.trade.margin.modal.delegation.default-account-name')}
									</Body>
									<Body color="secondary">{truncateAddress(address)}</Body>
								</FlexDivCol>
							</FlexDivRow>
							<FlexDivRowCentered columnGap="5px">
								{address === selectedSubAccountAddress ? (
									<>
										<Body color="secondary" size="xsmall">
											{t('futures.market.trade.margin.modal.delegation.connected')}
										</Body>
										<ConnectedIcon />
									</>
								) : (
									<>
										<Body color="secondary" size="xsmall">
											{t('futures.market.trade.margin.modal.delegation.connect')}
										</Body>
										<DisconnectedIcon />
									</>
								)}
							</FlexDivRowCentered>
						</SubAccountRowContainer>
					))
				)}
			</FlexDivCol>
		</>
	)
}

const NoDataContainer = styled(FlexDivColCentered)`
	margin: 25px 0;
	${media.lessThan('md')`
		height: 150px;
		margin-bottom: 80px;
	`}
`

const SubAccountRowContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	padding: 8px 0;
`

export default SubAccountsManagement
